import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { supabase } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import TicketViewUser from './TicketViewUser';
import ErrorBoundary from '../Common/ErrorBoundary';

/**
 * TicketRoute (Container)
 * Responsibilities:
 * 1. Resolve ticketId from URL
 * 2. Fetch ticket data (local cache or direct Supabase)
 * 3. Handle Loading / Not Found states
 * 4. Render Presentation Component only when data is ready
 */
const TicketRoute = () => {
  const { id: ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tickets, loading: contextLoading } = useTickets();

  // DEBUG: Track renders
  console.log('[TicketRoute] Render. TicketID:', ticketId, 'User:', user?.id);

  const [directTicket, setDirectTicket] = useState(null);
  const [loadingDirect, setLoadingDirect] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // 1. Resolve Ticket
  // First try to find in context (fastest)
  const localTicket = tickets?.find(t => String(t.id) === String(ticketId));
  const ticket = localTicket || directTicket;

  // 2. Direct Fetch Fallback
  // If not in context and not loading context, try direct fetch
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Reset state on ID change
    setDirectTicket(null);
    setNotFound(false);
    hasFetchedRef.current = false;
  }, [ticketId]);

  useEffect(() => {
    const fetchDirect = async () => {
      if (!ticketId) return;
      if (localTicket) return; // Already have it
      if (contextLoading) return; // Wait for context
      if (hasFetchedRef.current) return; // Already tried

      console.log('[TicketRoute] Fetching directly for:', ticketId);
      hasFetchedRef.current = true;
      setLoadingDirect(true);

      try {
        const columns = [
          'id', 'custom_id', 'display_id', 'title', 'description', 'category',
          'department', 'priority', 'status', 'type', 'city', 'user_id',
          'user_discord_id', 'user_name', 'user_avatar', 'player_id',
          'character_name', 'assigned_to', 'assigned_name', 'assigned_avatar',
          'auxiliary_status', 'assignees', 'responses', 'logs',
          'escalation_history', 'form_data', 'metadata', 'feedback',
          'created_at', 'last_update', 'bot_state', 'is_bot_typing', 'linked_ticket_id'
        ].join(',');

        const { data, error } = await supabase
          .from('tickets')
          .select(columns)
          .eq('id', ticketId)
          .single();

        if (error || !data) {
          console.error('[TicketRoute] Fetch failed:', error);
          setNotFound(true);
        } else {
          // Normalize
          const normalized = {
            ...data,
            customId: data.custom_id || data.customId,
            displayId: data.display_id || data.displayId,
            userId: data.user_id || data.userId,
            userDiscordId: data.user_discord_id || data.userDiscordId,
            userName: data.user_name || data.userName,
            userAvatar: data.user_avatar || data.userAvatar,
            playerId: data.player_id || data.playerId,
            characterName: data.character_name || data.characterName,
            assignedTo: data.assigned_to || data.assignedTo,
            assignedName: data.assigned_name || data.assignedName,
            assignedAvatar: data.assigned_avatar || data.assignedAvatar,
            auxiliaryStatus: data.auxiliary_status || data.auxiliaryStatus || [],
            formData: data.form_data || data.formData || {},
            escalationHistory: data.escalation_history || data.escalationHistory || [],
            linkedTicketId: data.linked_ticket_id || data.linkedTicketId,
            createdAt: data.created_at || data.createdAt,
            lastUpdate: data.last_update || data.lastUpdate,
            botState: data.bot_state || data.botState,
            isBotTyping: data.is_bot_typing || data.isBotTyping
          };

          // 🔒 PERMISSION GUARD: Regular users cannot see internal escalations
          const isInternal = normalized.category === 'escalacao_interna' || normalized.type === 'escalation';
          if (isInternal && user?.accessLevel === 'user') {
            console.warn('[TicketRoute] Access denied: User attempted to view escalation');
            setNotFound(true);
          } else {
            setDirectTicket(normalized);
          }
        }
      } catch (err) {
        console.error('[TicketRoute] Unexpected error:', err);
        setNotFound(true);
      } finally {
        setLoadingDirect(false);
      }
    };

    fetchDirect();
  }, [ticketId, localTicket, contextLoading]);

  // 3. Silent Refresh Interval (20s)
  // Ensures new responses appear even if realtime has delays
  const { refreshTickets } = useTickets();

  useEffect(() => {
    if (!ticketId) return;

    const interval = setInterval(() => {
      refreshTickets(false); // Silent refresh
    }, 20000);

    return () => clearInterval(interval);
  }, [ticketId, refreshTickets]);

  // 4. Determine Final Loading State
  // We are loading if context is loading OR if we are fetching directly
  const isLoading = contextLoading || loadingDirect;

  // 4. Guards
  if (isLoading && !ticket) {
    return (
      <div className="w-full h-[50vh] flex flex-col items-center justify-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mb-4" />
        <p className="font-bold">Carregando informações do ticket...</p>
      </div>
    );
  }

  if (notFound && !ticket) {
    return (
      <div className="w-full h-[50vh] flex flex-col items-center justify-center text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="font-bold text-white mb-2">Ticket não encontrado</p>
        <p className="text-sm text-gray-400 mb-4">O ticket #{ticketId} não existe ou você não tem permissão.</p>
        <Button variant="outline" onClick={() => navigate('/tickets')}>
          Voltar para Lista
        </Button>
      </div>
    );
  }

  // Fallback safety if nothing loaded (should be caught by notFound logic but acts as failsafe)
  if (!ticket) {
    // If we are not loading, and have no ticket, and notFound hasn't triggered yet (edge case), show loading
    return (
      <div className="w-full h-[50vh] flex flex-col items-center justify-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mb-4" />
        <p className="font-bold">Sincronizando...</p>
      </div>
    );
  }

  // 5. Render View
  // Use standardized isStaff check as requested
  const isStaff = Boolean(user?.isStaff || user?.is_staff || ['suporte', 'moderador', 'programador', 'admin', 'head_staff', 'suporte_compras', 'suporte_descontos', 'super_moderador', 'head_suporte', 'head_moderacao', 'direcao', 'analista_qualidade'].includes(user?.accessLevel));

  // Instrumentation
  console.log('[TicketRoute] Ready to Render. State:', {
    ticketId,
    hasTicket: !!ticket,
    loading: isLoading,
    isStaff,
    accessLevel: user?.accessLevel
  });

  return (
    <ErrorBoundary>
      <TicketViewUser ticket={ticket} isStaff={isStaff} />
    </ErrorBoundary>
  );
};

export default TicketRoute;
